import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssignPlayersRequest,
  AttendanceMarkRequest,
  BulkAttendanceRequest,
  Training,
  TrainingCreateRequest,
  TrainingNoteCreateRequest,
  TrainingPlayerSettingsRequest,
  TrainingRecurringPaymentUpsertRequest,
  TrainingSessionCreateRequest,
  TrainingSessionUpdateRequest,
  TrainingUpdateRequest,
} from '../features/trainings/managementModel';
import { TrainingDataService } from '../services/TrainingDataService';
import { playerQueryKeys } from './usePlayerData';
import { teamQueryKeys } from './useTeamData';
import { tournamentQueryKeys } from './tournamentQueryKeys';
import { userPeopleQueryKeys } from './useUserPeopleData';

export const trainingManagementQueryKeys = {
  all: ['training-management'] as const,
  trainings: (userEmail?: string) => [...trainingManagementQueryKeys.all, 'trainings', userEmail ?? 'anonymous'] as const,
  training: (trainingId: string) => [...trainingManagementQueryKeys.all, 'training', trainingId] as const,
  players: (trainingId: string) => [...trainingManagementQueryKeys.all, 'players', trainingId] as const,
  sessions: (trainingId: string) => [...trainingManagementQueryKeys.all, 'sessions', trainingId] as const,
  attendance: (sessionId: string) => [...trainingManagementQueryKeys.all, 'attendance', sessionId] as const,
  recurringPayments: (trainingId: string) => [...trainingManagementQueryKeys.all, 'recurring-payments', trainingId] as const,
  pendingInvitations: (userEmail?: string) => [...trainingManagementQueryKeys.all, 'pending-invitations', userEmail ?? 'anonymous'] as const,
  notes: (trainingId: string, publicOnly = false) =>
    [...trainingManagementQueryKeys.all, 'notes', trainingId, publicOnly ? 'public' : 'all'] as const,
  playerNotes: (playerId: string, publicOnly = false) =>
    [...trainingManagementQueryKeys.all, 'player-notes', playerId, publicOnly ? 'public' : 'all'] as const,
  historyRoot: () => [...trainingManagementQueryKeys.all, 'history'] as const,
  history: (trainingIdsSignature: string) =>
    [...trainingManagementQueryKeys.all, 'history', trainingIdsSignature] as const,
};

export function useTrainingsQuery(userEmail?: string, enabled = true) {
  return useQuery({
    queryKey: trainingManagementQueryKeys.trainings(userEmail),
    queryFn: () => TrainingDataService.getUserTrainings(userEmail!),
    enabled: enabled && !!userEmail,
    refetchOnWindowFocus: false,
  });
}

export function useTrainingQuery(trainingId?: string, enabled = true) {
  return useQuery({
    queryKey: trainingManagementQueryKeys.training(trainingId ?? 'missing'),
    queryFn: () => TrainingDataService.getTraining(trainingId!),
    enabled: enabled && !!trainingId,
    refetchOnWindowFocus: false,
  });
}

export function useTrainingPlayersQuery(trainingId?: string, enabled = true) {
  return useQuery({
    queryKey: trainingManagementQueryKeys.players(trainingId ?? 'missing'),
    queryFn: () => TrainingDataService.getTrainingPlayers(trainingId!),
    enabled: enabled && !!trainingId,
    refetchOnWindowFocus: false,
  });
}

export function useTrainingSessionsQuery(trainingId?: string, enabled = true) {
  return useQuery({
    queryKey: trainingManagementQueryKeys.sessions(trainingId ?? 'missing'),
    queryFn: () => TrainingDataService.getTrainingSessions(trainingId!),
    enabled: enabled && !!trainingId,
    refetchOnWindowFocus: false,
  });
}

export function useTrainingRecurringPaymentsQuery(trainingId?: string, enabled = true) {
  return useQuery({
    queryKey: trainingManagementQueryKeys.recurringPayments(trainingId ?? 'missing'),
    queryFn: () => TrainingDataService.getTrainingRecurringPayments(trainingId!),
    enabled: enabled && !!trainingId,
    refetchOnWindowFocus: false,
  });
}

export function useTrainingNotesQuery(trainingId?: string, publicOnly = false, enabled = true) {
  return useQuery({
    queryKey: trainingManagementQueryKeys.notes(trainingId ?? 'missing', publicOnly),
    queryFn: () => TrainingDataService.getTrainingNotes(trainingId!, publicOnly),
    enabled: enabled && !!trainingId,
    refetchOnWindowFocus: false,
  });
}

export function usePlayerTrainingNotesQuery(playerId?: string, publicOnly = false, enabled = true) {
  return useQuery({
    queryKey: trainingManagementQueryKeys.playerNotes(playerId ?? 'missing', publicOnly),
    queryFn: () => TrainingDataService.getPlayerTrainingNotes(playerId!, publicOnly),
    enabled: enabled && !!playerId,
    refetchOnWindowFocus: false,
  });
}

export function useTrainingAttendanceQuery(sessionId?: string, enabled = true) {
  return useQuery({
    queryKey: trainingManagementQueryKeys.attendance(sessionId ?? 'missing'),
    queryFn: () => TrainingDataService.getSessionAttendance(sessionId!),
    enabled: enabled && !!sessionId,
    refetchOnWindowFocus: false,
  });
}

export function usePendingTrainingPlayerInvitationsQuery(userEmail?: string, enabled = true) {
  return useQuery({
    queryKey: trainingManagementQueryKeys.pendingInvitations(userEmail),
    queryFn: () => TrainingDataService.getPendingTrainingPlayerInvitations(userEmail!),
    enabled: enabled && !!userEmail,
    refetchOnWindowFocus: false,
  });
}

export function useTrainingAttendanceHistoryQuery(trainings: Training[], enabled = true) {
  const trainingIdsSignature = trainings
    .map((training) => training.id)
    .filter(Boolean)
    .sort()
    .join('|');

  return useQuery({
    queryKey: trainingManagementQueryKeys.history(trainingIdsSignature || 'empty'),
    queryFn: () => TrainingDataService.getTrainingAttendanceHistory(trainings),
    enabled: enabled && trainings.length > 0,
    refetchOnWindowFocus: false,
  });
}

function invalidateTrainingManagement(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient.invalidateQueries({ queryKey: trainingManagementQueryKeys.all });
}

function invalidateClaimedPlayerState(queryClient: ReturnType<typeof useQueryClient>, email?: string) {
  void invalidateTrainingManagement(queryClient);
  if (!email) {
    return;
  }

  void queryClient.invalidateQueries({ queryKey: playerQueryKeys.management(email) });
  void queryClient.invalidateQueries({ queryKey: userPeopleQueryKeys.inventory(email) });
  void queryClient.invalidateQueries({ queryKey: teamQueryKeys.managed(email) });
  void queryClient.invalidateQueries({ queryKey: tournamentQueryKeys.userStats(email) });
  void queryClient.invalidateQueries({ queryKey: tournamentQueryKeys.userMatches('empty') });
}

export function useCreateTrainingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: TrainingCreateRequest) => TrainingDataService.createTraining(payload),
    onSuccess: () => {
      void invalidateTrainingManagement(queryClient);
    },
  });
}

export function useUpdateTrainingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ trainingId, payload }: { trainingId: string; payload: TrainingUpdateRequest }) =>
      TrainingDataService.updateTraining(trainingId, payload),
    onSuccess: () => {
      void invalidateTrainingManagement(queryClient);
    },
  });
}

export function useDeleteTrainingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (trainingId: string) => TrainingDataService.deleteTraining(trainingId),
    onSuccess: () => {
      void invalidateTrainingManagement(queryClient);
    },
  });
}

export function useAssignPlayersToTrainingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ trainingId, payload }: { trainingId: string; payload: AssignPlayersRequest }) =>
      TrainingDataService.assignPlayersToTraining(trainingId, payload),
    onSuccess: () => {
      void invalidateTrainingManagement(queryClient);
    },
  });
}

export function useUpdateTrainingPlayerSettingsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ trainingId, playerId, payload }: {
      trainingId: string;
      playerId: string;
      payload: TrainingPlayerSettingsRequest;
    }) => TrainingDataService.updateTrainingPlayerSettings(trainingId, playerId, payload),
    onSuccess: () => {
      void invalidateTrainingManagement(queryClient);
    },
  });
}

export function useClaimTrainingPlayerInvitationsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) => TrainingDataService.claimTrainingPlayerInvitations(email),
    onSuccess: (_, email) => {
      invalidateClaimedPlayerState(queryClient, email);
    },
  });
}

export function useCreateTrainingSessionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ trainingId, payload }: { trainingId: string; payload: TrainingSessionCreateRequest }) =>
      TrainingDataService.createTrainingSession(trainingId, payload),
    onSuccess: () => {
      void invalidateTrainingManagement(queryClient);
    },
  });
}

export function useDeleteTrainingSessionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ trainingId, sessionId }: { trainingId: string; sessionId: string }) =>
      TrainingDataService.deleteTrainingSession(trainingId, sessionId),
    onSuccess: () => {
      void invalidateTrainingManagement(queryClient);
    },
  });
}

export function useUpdateTrainingSessionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ trainingId, sessionId, payload }: {
      trainingId: string;
      sessionId: string;
      payload: TrainingSessionUpdateRequest;
    }) => TrainingDataService.updateTrainingSession(trainingId, sessionId, payload),
    onSuccess: () => {
      void invalidateTrainingManagement(queryClient);
    },
  });
}

export function useAssignPlayersToSessionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, payload }: { sessionId: string; payload: AssignPlayersRequest }) =>
      TrainingDataService.assignPlayersToSession(sessionId, payload),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: trainingManagementQueryKeys.attendance(variables.sessionId) });
      await queryClient.invalidateQueries({ queryKey: trainingManagementQueryKeys.historyRoot() });
    },
  });
}

export function useMarkTrainingAttendanceMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, payload }: { sessionId: string; payload: AttendanceMarkRequest }) =>
      TrainingDataService.markAttendance(sessionId, payload),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: trainingManagementQueryKeys.attendance(variables.sessionId) });
      await queryClient.invalidateQueries({ queryKey: trainingManagementQueryKeys.historyRoot() });
    },
  });
}

export function useBulkMarkTrainingAttendanceMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, payload }: { sessionId: string; payload: BulkAttendanceRequest }) =>
      TrainingDataService.bulkMarkAttendance(sessionId, payload),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: trainingManagementQueryKeys.attendance(variables.sessionId) });
      await queryClient.invalidateQueries({ queryKey: trainingManagementQueryKeys.historyRoot() });
    },
  });
}

export function useUpsertTrainingRecurringPaymentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ trainingId, payload }: { trainingId: string; payload: TrainingRecurringPaymentUpsertRequest }) =>
      TrainingDataService.upsertTrainingRecurringPayment(trainingId, payload),
    onSuccess: () => {
      void invalidateTrainingManagement(queryClient);
    },
  });
}

export function useCreateTrainingNoteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ trainingId, payload }: { trainingId: string; payload: TrainingNoteCreateRequest }) =>
      TrainingDataService.createTrainingNote(trainingId, payload),
    onSuccess: () => {
      void invalidateTrainingManagement(queryClient);
    },
  });
}

export function useUpdateTrainingNoteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ trainingId, noteId, payload }: { trainingId: string; noteId: string; payload: TrainingNoteCreateRequest }) =>
      TrainingDataService.updateTrainingNote(trainingId, noteId, payload),
    onSuccess: () => {
      void invalidateTrainingManagement(queryClient);
    },
  });
}

export function useDeleteTrainingNoteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ trainingId, noteId }: { trainingId: string; noteId: string }) =>
      TrainingDataService.deleteTrainingNote(trainingId, noteId),
    onSuccess: () => {
      void invalidateTrainingManagement(queryClient);
    },
  });
}
